import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { useTheme } from '../../context/ThemeProvider';
import { getColorSchemePalette } from '../../context/colorSchemes';
import { useDiagramExport } from '../../hooks/useDiagramExport';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import useDownloadFile from '../../hooks/useDownloadFile';
import useNativeFullscreen from '../../hooks/useNativeFullscreen';
import VizCopyButton from '../shared/Viz/VizCopyButton';
import VizDownloadButton from '../shared/Viz/VizDownloadButton';
import VizActionBar from '../shared/Viz/VizActionBar';
import logger from '../../services/Logger';
import { useOpenInNebula } from "../../hooks/useOpenInNebula";

// Initialize mermaid once
let mermaidInitialized = false;

const getMermaidThemeVariables = (palette, darkMode) => ({
  darkMode,
  background: palette.background,
  primaryColor: palette.muted,
  primaryTextColor: palette.foreground,
  primaryBorderColor: palette.border,
  secondaryColor: palette.sidebar,
  secondaryTextColor: palette.foreground,
  tertiaryColor: palette.muted,
  tertiaryTextColor: palette.foreground,
  textColor: palette.foreground,
  lineColor: palette.mutedForeground,
  edgeLabelBackground: palette.background,
});

const MermaidDiagram = ({ chart, hideControls = false, toggleElement = null }) => {
  const containerRef = useRef(null);
  const [svg, setSvg] = useState(null);
  const [renderError, setRenderError] = useState(null);
  const openInNebula = useOpenInNebula();
  const { theme, colorScheme } = useTheme();
  const palette = getColorSchemePalette(colorScheme, theme);
  const { ref: fullscreenRef, isFullscreen, enterFullscreen } = useNativeFullscreen();
  const { copied, copyToClipboard } = useCopyToClipboard({ resetDelay: 2000, caller: "MermaidDiagram" });
  const { downloadFile } = useDownloadFile({ caller: "MermaidDiagram" });

  const handleCopy = () => copyToClipboard(svg);
  const handleDownload = () => downloadFile(svg, `mermaid-diagram-${Date.now()}.svg`, "image/svg+xml");

  useEffect(() => {
    // Initialize mermaid with security and theme settings
    if (!mermaidInitialized) {
      mermaid.initialize({
        startOnLoad: false,
        theme: 'base',
        themeVariables: getMermaidThemeVariables(palette, theme === 'dark'),
        securityLevel: 'strict',
        fontFamily: 'Inter, system-ui, sans-serif',
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true,
          curve: 'basis',
        },
        sequence: {
          useMaxWidth: true,
          wrap: true,
        },
        gantt: {
          useMaxWidth: true,
        },
      });
      mermaidInitialized = true;
    }
  }, [theme, palette]);

  useEffect(() => {
    // Re-initialize when theme changes
    mermaid.initialize({
      startOnLoad: false,
      theme: 'base',
      themeVariables: getMermaidThemeVariables(palette, theme === 'dark'),
      securityLevel: 'strict',
      fontFamily: 'Inter, system-ui, sans-serif',
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
        curve: 'basis',
      },
      sequence: {
        useMaxWidth: true,
        wrap: true,
      },
      gantt: {
        useMaxWidth: true,
      },
    });
  }, [theme, palette]);

  useEffect(() => {
    let isMounted = true;
    const timer = setTimeout(async () => {
      if (!chart || !containerRef.current) return;

      try {
        // Generate a unique ID for this diagram
        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        
        // Render the diagram
        // mermaid.render throws if syntax is invalid
        const { svg: renderedSvg } = await mermaid.render(id, chart);
        
        if (isMounted) {
          setSvg(renderedSvg);
          setRenderError(null);
        }
      } catch (err) {
        if (isMounted) {
          logger.warn('MermaidDiagram', 'Mermaid rendering failed (possibly incomplete)', { error: err.message });
          setRenderError(err);
          // We do NOT clear the SVG here if we want to keep the last valid one, 
          // but for streaming it's better to fall back to code if the new chunk broke it.
          // Or better: if it fails, we show the code.
          setSvg(null); 
        }
      }
    }, 500); // Debounce rendering by 500ms to handle streaming

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [chart, palette]);

  // After SVG is set and the DOM has updated, capture a PNG and register it
  // for use by the message export feature.
  useDiagramExport(containerRef, chart, { delay: 100, trigger: svg, enabled: !!svg });

  // If we have a valid SVG, render it
  if (svg) {
    return (
      <div ref={fullscreenRef} className={`relative my-4 group ${isFullscreen ? "flex items-center justify-center w-full h-full bg-fill-white dark:bg-fill-hex-0d1117" : ""}`}>
        <style>{`
          .mermaid-container svg { shape-rendering: geometricPrecision; text-rendering: optimizeLegibility; }
          .mermaid-container svg text, .mermaid-container svg tspan { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
          .mermaid-container svg .label { font-family: Inter, system-ui, sans-serif !important; }
          :fullscreen .mermaid-container svg, :-webkit-full-screen .mermaid-container svg { width: 90vw !important; height: auto !important; max-height: 90vh; }
        `}</style>
        <div 
          ref={containerRef}
          className={`mermaid-container p-4 bg-fill-white dark:bg-fill-deep-surface border border-edge-gray-200 dark:border-edge-muted-control rounded-xl overflow-auto flex justify-center ${isFullscreen ? "w-full h-full max-w-none rounded-none border-0" : ""}`}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
        
        {/* Action Buttons — hidden in fullscreen, ESC exits */}
        {!isFullscreen && !hideControls && (
          <VizActionBar
            onFullscreen={enterFullscreen}
            leftContent={toggleElement}
            onOpenInNebula={openInNebula}
          >
            <VizCopyButton
              copied={copied}
              onClick={handleCopy}
              tooltip="Copy SVG"
            />
            <VizDownloadButton
              onClick={handleDownload}
              tooltip="Download SVG"
            />
          </VizActionBar>
        )}
      </div>
    );
  }

  // Fallback: Show the raw code if rendering failed or is pending
  // This ensures the user always sees *something* and can debug "missing backticks"
  return (
    <div ref={containerRef} className="relative rounded-md overflow-hidden my-3 group">
      <div className="p-4 bg-fill-gray-50 dark:bg-fill-hex-222222 border border-edge-gray-200 dark:border-edge-editor-reset-border rounded-md font-mono text-xs sm:text-sm text-ink-hex-0077aa dark:text-ink-copy-hover whitespace-pre-wrap break-words">
        {chart}
      </div>
      {renderError && (
        <div className="absolute top-0 right-0 p-1">
           <span className="text-[10px] text-ink-red-800 bg-fill-red-100 dark:bg-fill-red-900/30 px-1 rounded dark:text-ink-red-300">Invalid Syntax</span>
        </div>
      )}
      {!hideControls && toggleElement && (
        <VizActionBar leftContent={toggleElement} />
      )}
    </div>
  );
};

export default MermaidDiagram;
